import type { ReactNode } from 'react';
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from 'recharts';
import type { ChartSeries, RadarDatum, RadarDataLeadership } from './model';
import { computeRadarTickCount } from '@/components/blocks/AssessmentResults/shared/utils';
import { createRadialLabelTickRenderer } from '@/pages/ModuleLauncher/shared/utils';

export interface RadarChartCardProps {
    title?: string;
    data: (RadarDatum | RadarDataLeadership)[];
    maxValueData: number;
    showTeam?: boolean;
    variant?: 'default' | 'bare';
    height?: number;
    series?: ChartSeries[];
    footer?: ReactNode;
}

const RadarChartCard = ({
    title,
    data,
    maxValueData,
    showTeam,
    variant = 'default',
    height = 220,
    series,
    footer,
}: RadarChartCardProps) => {
    const wrapperClass =
        variant === 'bare' ? 'w-full h-full flex flex-col' : 'rounded-md bg-neutral-0 p-4 flex flex-col';

    const axisMaxValues = maxValueData;

    return (
        <div className={`${wrapperClass} pdf-avoid-break justify-center`}>
            {title && title.trim() && <h4 className="mb-2 text-xs font-semibold text-neutral-700">{title}</h4>}
            <div className="items-center justify-center">
                <ResponsiveContainer width="100%" height="100%" minHeight={height}>
                    <RadarChart data={data} margin={{ top: 30, right: 16, bottom: 16, left: 16 }}>
                        <PolarGrid stroke="var(--color-neutral-200)" gridType="circle" />
                        <PolarAngleAxis
                            dataKey="axis"
                            tick={createRadialLabelTickRenderer(22, {
                                fontSize: 12,
                                fill: 'var(--color-neutral-500)',
                                fontWeight: 500,
                            })}
                        />
                        {!series && (
                            <Radar
                                name={showTeam ? 'Team' : 'Individual'}
                                dataKey={showTeam ? 'team' : 'individual'}
                                stroke="var(--color-blue-500)"
                                fill="var(--color-blue-500)"
                                fillOpacity={0.25}
                                strokeWidth={2}
                            />
                        )}
                        {series &&
                            series.map(s => (
                                <Radar
                                    key={s.key}
                                    name={s.name}
                                    dataKey={s.key}
                                    stroke={s.stroke}
                                    fill={s.fill || s.stroke}
                                    fillOpacity={s.fillOpacity ?? 0.25}
                                    strokeWidth={2}
                                />
                            ))}
                        {series && (
                            <Legend
                                verticalAlign="bottom"
                                wrapperStyle={{
                                    fontSize: 14,
                                    color: 'var(--color-neutral-500)',
                                }}
                                formatter={value => <span style={{ color: 'var(--color-neutral-900)' }}>{value}</span>}
                            />
                        )}
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, axisMaxValues]}
                            tickCount={computeRadarTickCount(axisMaxValues)}
                            tick={{
                                fontSize: 10,
                                fill: 'var(--color-neutral-500)',
                                fontWeight: 500,
                                textAnchor: 'middle',
                                dominantBaseline: 'middle',
                                dy: -5,
                            }}
                        />
                        <Tooltip
                            formatter={(val: any, key: any) => [val, key]}
                            cursor={{ stroke: 'var(--color-neutral-300)' }}
                            contentStyle={{
                                fontSize: 16,
                                backgroundColor: 'var(--color-popover)',
                                borderColor: 'var(--color-neutral-300)',
                                color: 'var(--color-neutral-900)',
                            }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            {footer && <div className="mt-2 text-center text-base font-[550] text-neutral-800">{footer}</div>}
        </div>
    );
};

export default RadarChartCard;
